import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import RouvyDomainHint from './RouvyDomainHint';
import './RouvyReauthModal.css';

// Всплывающее окно с той же просьбой переподключить ROUVY, что и плашка в профиле
// (см. .rouvy-reauth-notice в Profile.js). Показывается один раз за сессию после входа,
// чтобы пользователь узнал о нехватке прав, даже если не заходит в профиль.
const RouvyReauthModal = () => {
  const { user, isAuthenticated } = useAuth();
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const userId = user?.id;
  const deactivated = user?.deactivated;

  useEffect(() => {
    if (!isAuthenticated || !userId || deactivated) return;

    const dismissKey = `rouvy_reauth_modal_dismissed_${userId}`;
    if (sessionStorage.getItem(dismissKey)) return;

    let cancelled = false;

    const checkRouvyConnection = async () => {
      try {
        const response = await axios.get('/api/rouvy/status');
        if (cancelled) return;

        if (response.data.connected && response.data.needs_reauthorization) {
          setIsOpen(true);
        }
      } catch (err) {
        console.error('Failed to check ROUVY connection:', err);
      }
    };

    checkRouvyConnection();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, userId, deactivated]);

  const close = () => {
    if (userId) {
      // Одного показа за сессию достаточно: плашка в профиле никуда не денется
      sessionStorage.setItem(`rouvy_reauth_modal_dismissed_${userId}`, '1');
    }
    setIsOpen(false);
  };

  // Переподключение — это обычная авторизация: callback перезапишет токен новыми правами
  const reconnectRouvy = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await axios.get('/api/rouvy/authorize');

      if (response.data.authorization_url) {
        window.location.href = response.data.authorization_url;
      }
    } catch (err) {
      console.error('ROUVY authorization error:', err);
      setError(err.response?.data?.error ||
        t('rouvy.authUrlError'));
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="rouvy-reauth-overlay" onClick={close}>
      <div className="rouvy-reauth-modal" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="rouvy-reauth-modal-close"
          onClick={close}
          aria-label={t('common.close')}
        >
          ×
        </button>

        <div className="rouvy-reauth-title">
          ⚠️ {t('rouvy.reauthTitle')}
        </div>
        <p className="rouvy-reauth-text">
          {t('rouvy.reauthText')}
        </p>

        {/* Кнопка ниже ведет на тот же Authorize, что и в профиле,
            поэтому предупреждение про домен .ru нужно и здесь */}
        <RouvyDomainHint className="rouvy-reauth-hint" />

        {error && <div className="rouvy-reauth-modal-error">{error}</div>}

        <div className="rouvy-reauth-modal-actions">
          <button
            onClick={reconnectRouvy}
            disabled={loading}
            className="btn btn-primary"
          >
            {loading ? t('rouvy.connecting') : t('rouvy.reconnect')}
          </button>
          <button
            onClick={close}
            disabled={loading}
            className="btn btn-secondary"
          >
            {t('common.later')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RouvyReauthModal;
